import React, { useState, useEffect } from "react";
import {
  getPopupAnnouncements,
  shouldShowAnnouncementPopup,
  dismissAnnouncementForToday
} from "../api/announcementApi";
import useTranslation from "../hooks/useTranslation";
import "./AnnouncementModal.css";

/**
 * 解析文本中的URL并转换为可点击链接
 * @param {string} text - 原始文本
 * @returns {Array} - React元素数组
 */
const parseTextWithLinks = (text) => {
  if (!text) return null;
  
  // URL正则匹配（只匹配标准URL字符，排除中文等宽字符）
  // 匹配 http/https 开头的URL 或者 域名格式的字符串
  const urlRegex = /(https?:\/\/[a-zA-Z0-9\-\._~:\/?#\[\]@!$&'()*+,;=%]+|[a-zA-Z0-9][-a-zA-Z0-9]*\.[a-zA-Z]{2,}[a-zA-Z0-9\-\._~:\/?#\[\]@!$&'()*+,;=%]*)/gi;
  
  const parts = [];
  let lastIndex = 0;
  let match;
  let keyIndex = 0;
  
  while ((match = urlRegex.exec(text)) !== null) {
    // 添加链接前的普通文本
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    
    // 处理链接
    let url = match[0];
    let displayUrl = url;
    
    // 如果链接没有协议，添加 https://
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    
    // 创建可点击的链接元素
    parts.push(
      <a 
        key={`link-${keyIndex++}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="announcement-link"
        onClick={(e) => e.stopPropagation()}
      >
        🔗 {displayUrl}
      </a>
    );
    
    // 检查链接后是否有内容，如果有，添加换行
    // 我们假设如果链接后面紧跟内容，应该是希望换行显示的提示语
    if (match.index + match[0].length < text.length) {
      parts.push(<br key={`br-${keyIndex++}`} />);
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  // 添加最后剩余的文本
  if (lastIndex < text.length) {
    // 如果前面加了br，这里的文本就是新的一行
    const remainingText = text.substring(lastIndex);
    // 如果剩余文本以换行符开头，去掉它，因为我们已经加了br
    // 不过一般来说 text 是按行 split 的，所以这里不会有换行符
    parts.push(remainingText);
  }
  
  return parts.length > 0 ? parts : text;
};

/**
 * 公告弹窗组件
 * 支持从API获取公告、自动弹出、今日不再提示等功能
 * 
 * 弹出机制：
 * - 每次用户进入页面都会自动弹出公告
 * - 只有当用户点击"我知道了"按钮后，当天不再弹出
 * - 第二天会重新弹出
 */
const AnnouncementModal = ({ 
  isOpen, 
  onClose, 
  autoPopup = false,
  onAutoPopupComplete
}) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);
  const tr = useTranslation();

  // 加载公告数据
  const loadAnnouncements = async () => {
    setLoading(true);
    try {
      const response = await getPopupAnnouncements();
      if (response.success && response.data && response.data.length > 0) {
        setAnnouncements(response.data);
        return true;
      }
      return false;
    } catch (error) {
      console.error('加载公告失败:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // 自动弹出逻辑 - 每次进入都检查是否需要弹出
  useEffect(() => {
    const checkAndShowPopup = async () => {
      let isShowing = false;
      // 检查是否应该显示弹窗（用户今天没有点击过"我知道了"）
      if (autoPopup && shouldShowAnnouncementPopup()) {
        const hasAnnouncements = await loadAnnouncements();
        if (hasAnnouncements) {
          setShouldShow(true);
          isShowing = true;
        }
      }
      if (onAutoPopupComplete) {
        onAutoPopupComplete(isShowing);
      }
    };

    checkAndShowPopup();
  }, [autoPopup, onAutoPopupComplete]);

  // 手动打开时加载公告（通过喇叭按钮）
  useEffect(() => {
    if (isOpen && !autoPopup) {
      loadAnnouncements();
    }
  }, [isOpen, autoPopup]);

  // 点击遮罩层不关闭，强制用户点击按钮
  const handleOverlayClick = (e) => {
    // 不做任何事情，用户必须点击"我知道了"按钮才能关闭
    e.stopPropagation();
  };

  // 点击"我知道了"按钮 - 记录今日不再提示并关闭
  const handleConfirm = () => {
    dismissAnnouncementForToday();
    setShouldShow(false);
    if (onClose) {
      onClose();
    }
  };

  // 点击关闭按钮（X）- 也记录今日不再提示
  const handleCloseButton = () => {
    dismissAnnouncementForToday();
    setShouldShow(false);
    if (onClose) {
      onClose();
    }
  };

  // 决定是否显示
  const isVisible = isOpen || shouldShow;

  if (!isVisible) {
    return null;
  }

  return (
    <div className="announcement-modal-overlay" onClick={handleOverlayClick}>
      <div className="announcement-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="announcement-close-btn" onClick={handleCloseButton} title={tr.closeHint || "关闭（今日不再提示）"}>
          <i className="ri-close-line"></i>
        </button>
        
        <div className="announcement-header">
          <i className="ri-megaphone-line announcement-icon"></i>
          <h2>{tr.systemAnnouncement || "系统公告"}</h2>
        </div>

        {loading ? (
          <div className="announcement-loading">
            <i className="ri-loader-4-line spinning"></i>
            <span>{tr.loading || "加载中..."}</span>
          </div>
        ) : announcements.length > 0 ? (
          <div className="announcement-list">
            {announcements.map((announcement, index) => (
              <div 
                key={announcement.id || index} 
                className={`announcement-item ${index === 0 ? 'highlight' : ''}`}
              >
                <div className="announcement-item-header">
                  <h3>
                    <i className="ri-notification-3-line"></i>
                    {announcement.title}
                  </h3>
                  {announcement.created_at && (
                    <span className="announcement-date">
                      {announcement.created_at.split(' ')[0]}
                    </span>
                  )}
                </div>
                <div className="announcement-content">
                  {announcement.content.split('\n').map((line, i) => (
                    <p key={i}>{parseTextWithLinks(line)}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="announcement-empty">
            <i className="ri-information-line"></i>
            <p>{tr.noAnnouncements || "暂无公告"}</p>
          </div>
        )}

        <div className="announcement-footer">
          <div className="announcement-hint">
            <i className="ri-information-line"></i>
            <span>{tr.dontShowAgainHint || "点击下方按钮后今日不再弹出"}</span>
          </div>
          <button className="announcement-confirm-btn" onClick={handleConfirm}>
            <i className="ri-check-line"></i>
            {tr.confirmKnow || "我知道了，不再提示"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementModal;
